import React, { useCallback, useRef, useState } from "react";
import ReactCrop, { Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { inkOnlyCanvas, removePaperCanvas } from "./signatureInk";
import ImageResizePanel, { ResizePreset } from "./ImageResizePanel";

// Generic image capture + crop + rotate control, with no external dependency.
// The operator pastes from the clipboard, uploads a file, or re-opens an image
// already on the record; drags a rectangle over the region to keep; optionally
// rotates in 90° steps; and the result is emitted as a base64 PNG data-URL.
//
// Nothing here ever uploads. The emitted data-URL is embedded straight into the
// HTML posted to /pan-form/add, so the applicant's own stored files (e.g.
// passportPhotoUrl, signaturePhotoUrl) are never modified by cropping here.
//
// Used for the signature (via SignatureInput), the passport photograph, and
// each attached document.

type Props = {
  value: string; // committed data-URL, or a remote URL ("" if none yet)
  onChange: (dataUrl: string) => void;
  /** Heading shown above the control. */
  label?: string;
  /** Longest side of the committed image, in px. Keeps the posted HTML small. */
  maxSize?: number;
  /** Preview box for the committed image. */
  previewMaxHeight?: number;
  previewMaxWidth?: number;
  /** Wording tweak for the empty state. */
  itemName?: string;
  /**
   * Offer the ink-only pass: drop the paper the image was photographed on and
   * trim the result to the strokes that are left — see `inkOnlyCanvas` for why
   * the printed signature box can want it.
   *
   * It is offered, not applied: the operator ticks "Ink only" in the Adjust
   * panel. Run automatically it also removed things nobody asked it to remove,
   * and the operator had no way back to the signature the applicant actually
   * uploaded.
   */
  inkOnly?: boolean;
  /**
   * Open straight into the crop/adjust workspace when a value is already set.
   *
   * Used where the control is only rendered because the operator asked to edit
   * (the attachment "Crop / Adjust" panel) — otherwise they land on a preview
   * and have to click a second time before the sliders appear, which reads as
   * the adjustment being missing.
   */
  autoEdit?: boolean;
  /**
   * Fired when the crop workspace opens and closes. The caller needs it when
   * the widget sits in a pinned strip: the workspace is far taller than the
   * preview, so a strip that stays pinned while cropping would cover the form.
   */
  onEditingChange?: (editing: boolean) => void;
  /**
   * The applicant's own upload. When the image in use is something else (a
   * copy edited or cleaned earlier and saved), a button opens the workspace on
   * this original instead — so an earlier edit that came out wrong can be
   * redone from scratch rather than re-processed on top of itself.
   */
  originalUrl?: string;
  /**
   * Documents only: open the workspace at Grayscale 100% so the page prints
   * black & white unless the operator slides it back. Off for the photo and
   * signature, which keep their own colour.
   */
  grayscaleByDefault?: boolean;
  /**
   * Photo / signature only: a "Resize" button that opens pixel + KB options
   * (ImageResizePanel) and downloads the result as `<resizeFileName>.jpg` for
   * the PAN portal. The image in the form is left as it is.
   */
  resizePreset?: ResizePreset;
  resizeFileName?: string;
};

/**
 * The crop rectangle, in the shape `react-image-crop` uses.
 *
 * The hand-rolled drag box this replaced could only be drawn once — there were
 * no handles, so a box that came out slightly wrong had to be redrawn from
 * scratch, and it could not be nudged. `react-image-crop` is the same control
 * the applicant-facing site uses (MOLFileUploader), so an operator who has seen
 * one has seen both: drag to draw, drag the edges or corners to resize, drag
 * the middle to move, with everything outside the box dimmed.
 */
type Rect = Crop;

/**
 * Tone adjustments applied to the working image. They are previewed with a CSS
 * `filter` (instant, no re-encode) and baked into the canvas via `ctx.filter`
 * when the crop is committed — so the emitted data-URL already carries them and
 * the PDF prints exactly what was previewed. No API change is involved.
 */
type Adjustments = {
  brightness: number; // %
  contrast: number; // %
  saturation: number; // %
  grayscale: number; // %
  threshold: boolean; // hard black/white pass for faded scans
  thresholdAt: number; // 0-255 cut-off used when `threshold` is on
  ink: boolean; // keep only the strokes (offered when `inkOnly` is set)
};

const NEUTRAL: Adjustments = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  grayscale: 0,
  threshold: false,
  thresholdAt: 160,
  ink: false,
};

const filterCss = (a: Adjustments) =>
  `brightness(${a.brightness}%) contrast(${a.contrast}%) saturate(${a.saturation}%) grayscale(${a.grayscale}%)`;

/**
 * How steep the preview's black/white step is.
 *
 * `feFuncX type="linear"` is `C' = slope * C + intercept`, clamped to [0,1], so
 * a steep enough line IS a step: at 2550 the whole transition happens inside a
 * tenth of one grey level, finer than an 8-bit image can express.
 */
const BW_SLOPE = 2550;

/**
 * Where the preview's step sits, as a 0-1 fraction.
 *
 * Half a level below the cut-off, so a pixel sitting exactly ON it goes white —
 * which is what the committed pass does (`lum >= thresholdAt`). The preview has
 * to agree with it level for level or the operator is adjusting one image and
 * printing another.
 */
const bwCut = (thresholdAt: number) => (thresholdAt - 0.5) / 255;

/**
 * Where a document's workspace starts (`grayscaleByDefault`): black & white,
 * Grayscale 100%. The operator can still slide it back down for a scan that
 * needs colour, and Reset returns here. The photo and signature keep NEUTRAL.
 */
const DOCUMENT_ADJ: Adjustments = { ...NEUTRAL, grayscale: 100 };

/** True when `a` is still the starting point — hides the Reset button. */
const isAtStart = (a: Adjustments, start: Adjustments) =>
  a.brightness === start.brightness &&
  a.contrast === start.contrast &&
  a.saturation === start.saturation &&
  a.grayscale === start.grayscale &&
  a.threshold === start.threshold &&
  a.ink === start.ink;

const ImageCropInput = ({
  value,
  onChange,
  label = "Image",
  maxSize = 700,
  previewMaxHeight = 70,
  previewMaxWidth = 220,
  itemName = "image",
  inkOnly = false,
  autoEdit = false,
  onEditingChange,
  originalUrl = "",
  grayscaleByDefault = false,
  resizePreset,
  resizeFileName = "image",
}: Props) => {
  const [showResize, setShowResize] = useState(false);
  // The pasted/uploaded image awaiting crop. Null once committed (or before load).
  const [rawImage, setRawImage] = useState<string | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  // Signature: "Ink only" starts ticked, so a newly uploaded signature comes
  // out with its paper removed (ink kept as it is) — the same pass the prefilled one
  // gets automatically (cleanSignatureImage) — without the operator having to
  // find the tick.
  const base = grayscaleByDefault ? DOCUMENT_ADJ : NEUTRAL;
  const start: Adjustments = inkOnly ? { ...base, ink: true } : base;
  const [adj, setAdj] = useState<Adjustments>(start);

  // Mount-time only: opening the workspace later, after the operator commits a
  // crop, would trap them in an edit loop they cannot leave.
  const didAutoEdit = useRef(false);
  React.useEffect(() => {
    if (autoEdit && value && !didAutoEdit.current) {
      didAutoEdit.current = true;
      setRawImage(value);
    }
  }, [autoEdit, value]);
  React.useEffect(() => {
    onEditingChange?.(!!rawImage);
  }, [rawImage, onEditingChange]);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const naturalRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  // Bumped on each load of the working image, so the ink preview below re-runs
  // once the pixels are actually available.
  const [loadTick, setLoadTick] = useState(0);
  /**
   * "Ink only" previewed live: the working image with its paper removed, laid
   * over the original on white. Without it the workspace showed the raw photo
   * (paper, shadow and all) and the removal only appeared after Crop & Use, so
   * it looked like the background was never removed. Not trimmed here — the
   * overlay must match the image underneath so the crop box still lines up.
   */
  const [inkPreview, setInkPreview] = useState<string | null>(null);
  React.useEffect(() => {
    const el = imgRef.current;
    if (!inkOnly || !adj.ink || !rawImage || !el || !naturalRef.current.w) {
      setInkPreview(null);
      return;
    }
    const timer = window.setTimeout(() => {
      try {
        const nat = naturalRef.current;
        const scale = Math.min(1, maxSize / Math.max(nat.w, nat.h));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(nat.w * scale));
        canvas.height = Math.max(1, Math.round(nat.h * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.filter = filterCss(adj);
        ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
        ctx.filter = "none";
        if (adj.threshold) {
          const im = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const d = im.data;
          for (let i = 0; i < d.length; i += 4) {
            const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
            d[i] = d[i + 1] = d[i + 2] = lum >= adj.thresholdAt ? 255 : 0;
          }
          ctx.putImageData(im, 0, 0);
        }
        const cleaned = removePaperCanvas(canvas);
        setInkPreview(cleaned ? cleaned.toDataURL("image/png") : null);
      } catch {
        setInkPreview(null); // tainted canvas — the original stays on show
      }
    }, 150);
    return () => window.clearTimeout(timer);
  }, [inkOnly, adj, rawImage, loadTick, maxSize]);

  // One filter per instance: the photo, the signature and a document can each
  // have a workspace open with its own cut-off. `useId` hands back a value with
  // colons in it — legal in an id, awkward inside `url(#…)`.
  const bwFilterId = `pf-bw-${React.useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

  /**
   * Live preview of the adjustment, as a CSS `filter`.
   *
   * The tone part is plain CSS. The black/white pass used to be FAKED here with
   * `grayscale(100%) contrast(400%)`, and the operator saw both consequences
   * directly: the cut-off slider moved nothing on screen (the approximation
   * does not contain it), and the committed image came out somewhere else,
   * because `applyCrop` thresholds at the cut-off the preview had ignored.
   *
   * It is the real pass now — luminance to grey, then a step at the cut-off —
   * run as an SVG filter in the same order `applyCrop` applies it (tone first,
   * then the step). So the slider works, and what is previewed is what is
   * baked in.
   */
  const previewFilter = adj.threshold
    ? `${filterCss(adj)} url(#${bwFilterId})`
    : filterCss(adj);

  const loadFromFile = useCallback((file: File | null | undefined) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRect(null);
      setRawImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const onPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        loadFromFile(items[i].getAsFile());
        e.preventDefault();
        return;
      }
    }
  };

  /**
   * Opening crop box: the WHOLE image, and free — no locked ratio.
   *
   * It used to open at 90% and, for the photograph, locked to the passport
   * 3.5:4.5. Both got in the way: the 90% box quietly shaved a strip off every
   * edge of an image that was already framed correctly, and a locked ratio
   * hides the library's edge handles (`.ReactCrop--fixed-aspect`), which is why
   * the photograph had no mid-edge grips while the signature did. Starting at
   * 100% means "leave it alone" costs no drag at all, and a free box gives
   * corner AND edge handles on every image the admin crops.
   */
  const initialCrop = (w: number, h: number): Rect => ({
    unit: "px",
    x: 0,
    y: 0,
    width: w,
    height: h,
  });

  const taintWarning = () =>
    // eslint-disable-next-line no-alert
    alert(
      `Couldn't process this ${itemName} due to a browser security restriction. Please upload or paste it instead.`
    );

  /**
   * Re-bake the working image through a canvas under a rotation / flip.
   *
   * Transforming the bitmap itself (rather than CSS-transforming the preview)
   * means the crop maths below needs no orientation awareness at all — the
   * displayed image and its natural dimensions always agree. PNG is lossless,
   * so repeated turns do not degrade the image.
   *
   * `quarterTurns` is in 90° steps, positive clockwise. `flip` mirrors the
   * result: "h" left-to-right, "v" top-to-bottom.
   */
  const transformImage = (
    quarterTurns: number,
    flip?: "h" | "v"
  ) => {
    const el = imgRef.current;
    if (!el) return;
    const nat = naturalRef.current;
    // A quarter turn swaps the canvas's own width and height; a half turn or a
    // flip keeps them.
    const swaps = Math.abs(quarterTurns) % 2 === 1;
    const canvas = document.createElement("canvas");
    canvas.width = swaps ? nat.h : nat.w;
    canvas.height = swaps ? nat.w : nat.h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    if (quarterTurns) ctx.rotate((quarterTurns * Math.PI) / 2);
    if (flip) ctx.scale(flip === "h" ? -1 : 1, flip === "v" ? -1 : 1);
    ctx.drawImage(el, -nat.w / 2, -nat.h / 2, nat.w, nat.h);
    try {
      const next = canvas.toDataURL("image/png");
      // The box belongs to the old orientation, so it goes: a fresh one opens
      // on the whole of the new image (see `initialCrop`).
      setRect(null);
      setRawImage(next);
    } catch {
      taintWarning();
    }
  };

  const applyCrop = (fullImage: boolean) => {
    const el = imgRef.current;
    if (!el) return;
    const nat = naturalRef.current;
    const scaleX = nat.w / el.clientWidth;
    const scaleY = nat.h / el.clientHeight;

    let sx = 0,
      sy = 0,
      sw = nat.w,
      sh = nat.h;
    if (!fullImage && rect && rect.width > 4 && rect.height > 4) {
      // The box is kept in px against the displayed image, but a `%` crop can
      // still arrive (the library switches units when the box is dragged past
      // an edge), so convert against the natural size in that case.
      const pct = rect.unit === "%";
      sx = pct ? (rect.x / 100) * nat.w : rect.x * scaleX;
      sy = pct ? (rect.y / 100) * nat.h : rect.y * scaleY;
      sw = pct ? (rect.width / 100) * nat.w : rect.width * scaleX;
      sh = pct ? (rect.height / 100) * nat.h : rect.height * scaleY;
    }

    // Downscale so the longest side is at most maxSize px — keeps the posted
    // HTML small (a raw phone photo can be several MB, which bloats the PDF and
    // generation time) while staying crisp for the print box.
    const scale = Math.min(1, maxSize / Math.max(sw, sh));
    let canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(sw * scale));
    canvas.height = Math.max(1, Math.round(sh * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Bake the brightness/contrast/saturation/grayscale adjustment in. Anything
    // the operator saw in the preview is now part of the pixels.
    ctx.filter = filterCss(adj);
    ctx.drawImage(el, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    ctx.filter = "none";

    // Hard black/white pass. `ctx.filter` has no threshold, so this one runs
    // over the pixels — worth it for faded or grey photocopied documents.
    if (adj.threshold) {
      try {
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = img.data;
        for (let i = 0; i < d.length; i += 4) {
          const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          const bw = lum >= adj.thresholdAt ? 255 : 0;
          d[i] = d[i + 1] = d[i + 2] = bw;
        }
        ctx.putImageData(img, 0, 0);
      } catch {
        taintWarning();
        return;
      }
    }

    // Signature only: separate the strokes from the paper they were
    // photographed on and trim to them, so the printed box holds the signature
    // and nothing else.
    if (inkOnly && adj.ink) {
      try {
        const cleaned = inkOnlyCanvas(canvas);
        if (cleaned) canvas = cleaned;
      } catch {
        taintWarning();
        return;
      }
    }

    let dataUrl: string;
    try {
      dataUrl = canvas.toDataURL("image/png");
    } catch {
      // Canvas got tainted (remote image without CORS) — can't export it.
      taintWarning();
      return;
    }
    onChange(dataUrl);
    setRawImage(null);
    setRect(null);
    setAdj(start);
  };

  const btn: React.CSSProperties = {
    fontSize: "12px",
    padding: "5px 12px",
    cursor: "pointer",
  };

  /** Square, glyph-sized: the orientation toolbar below Crop & Use. */
  const iconBtn: React.CSSProperties = {
    fontSize: "16px",
    lineHeight: 1,
    width: "30px",
    height: "28px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  };

  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "6px" }}>
        {label}
      </div>

      {/* Committed image preview (not currently cropping) */}
      {value && !rawImage && (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <img
            src={value}
            alt={label}
            style={{
              maxHeight: `${previewMaxHeight}px`,
              maxWidth: `${previewMaxWidth}px`,
              border: "1px solid #ccc",
              background: "#fff",
              padding: "2px",
            }}
          />
          <button
            type="button"
            onClick={() => {
              // Load the current image back into the crop tool. Works for both
              // pasted data-URLs and a prefilled remote URL (the API serves
              // uploads with CORS, so the crop canvas stays clean).
              setRect(null);
              setRawImage(value);
            }}
            style={{ ...btn, padding: "4px 10px" }}
          >
            Crop / Adjust
          </button>
          <button
            type="button"
            onClick={() => onChange("")}
            style={{ ...btn, padding: "4px 10px" }}
          >
            Remove / Change
          </button>
          {resizePreset && (
            <button
              type="button"
              title="Set pixels and KB, and download as JPG for the PAN portal"
              onClick={() => setShowResize((v) => !v)}
              style={{ ...btn, padding: "4px 10px" }}
            >
              Resize
            </button>
          )}
          {originalUrl && value !== originalUrl && (
            <button
              type="button"
              title={`Open the applicant's original ${itemName} and edit it again`}
              onClick={() => {
                setRect(null);
                setAdj(start);
                setRawImage(originalUrl);
              }}
              style={{ ...btn, padding: "4px 10px" }}
            >
              Original se dobara
            </button>
          )}
        </div>
      )}

      {resizePreset && showResize && value && !rawImage && (
        <ImageResizePanel
          src={value}
          preset={resizePreset}
          fileName={resizeFileName}
          onClose={() => setShowResize(false)}
        />
      )}

      {/* Empty state — paste or upload */}
      {!value && !rawImage && (
        <div
          tabIndex={0}
          onPaste={onPaste}
          style={{
            border: "1px dashed #aaa",
            borderRadius: "4px",
            padding: "12px",
            fontSize: "12px",
            color: "#555",
            outline: "none",
          }}
        >
          <div style={{ marginBottom: "8px" }}>
            Click here and <b>paste</b> the {itemName} (Ctrl/Cmd+V), or upload:
          </div>
          <input
            type="file"
            accept="image/*"
            style={{ fontSize: "12px" }}
            onChange={(e) => loadFromFile(e.target.files?.[0])}
          />
        </div>
      )}

      {/* Crop + rotate workspace */}
      {rawImage && (
        <div>
          {/* The black/white step `previewFilter` points at. sRGB
              interpolation, not the linearRGB default: `applyCrop` thresholds
              raw 0-255 bytes, and linear light would put the preview's step at
              a different brightness than the committed one. */}
          <svg
            aria-hidden
            focusable="false"
            width="0"
            height="0"
            style={{ position: "absolute", width: 0, height: 0 }}
          >
            <filter id={bwFilterId} colorInterpolationFilters="sRGB">
              {/* Rec. 601 luminance into all three channels — the same weights
                  the committed pass uses. */}
              <feColorMatrix
                type="matrix"
                values="0.299 0.587 0.114 0 0 0.299 0.587 0.114 0 0 0.299 0.587 0.114 0 0 0 0 0 1 0"
              />
              <feComponentTransfer>
                <feFuncR
                  type="linear"
                  slope={BW_SLOPE}
                  intercept={-BW_SLOPE * bwCut(adj.thresholdAt)}
                />
                <feFuncG
                  type="linear"
                  slope={BW_SLOPE}
                  intercept={-BW_SLOPE * bwCut(adj.thresholdAt)}
                />
                <feFuncB
                  type="linear"
                  slope={BW_SLOPE}
                  intercept={-BW_SLOPE * bwCut(adj.thresholdAt)}
                />
              </feComponentTransfer>
            </filter>
          </svg>
          <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>
            The box starts on the whole image and is free — drag any corner or
            edge to frame the {itemName} (drag inside it to move it), adjust the
            tone below, then click <b>Crop &amp; Use</b> (or use the full image).
          </div>
          {/* The workspace takes the width it is given. It was capped at 460px
              wide and 320px tall, which on a full-screen form window showed a
              document scan too small to place a crop on accurately. */}
          <div
            style={{
              display: "block",
              width: "100%",
              border: "1px solid #ccc",
              lineHeight: 0,
              textAlign: "center",
            }}
          >
            <ReactCrop
              crop={rect ?? undefined}
              // No `aspect`: every crop in the admin is free, so the library
              // keeps its mid-edge handles as well as its corners.
              ruleOfThirds
              // A stray click on the image must not silently drop the box —
              // "Crop & Use" would then quietly export the whole image.
              keepSelection
              // px, so the box stays put while the tone sliders re-render, and
              // so `applyCrop` scales it against the displayed size directly.
              onChange={(pixelCrop) => setRect(pixelCrop)}
            >
              <img
                ref={imgRef}
                src={rawImage}
                alt="To crop"
                draggable={false}
                // Allow a clean (untainted) canvas read when cropping a remote
                // URL — the API serves uploads with Access-Control-Allow-Origin,
                // so this keeps toDataURL working.
                crossOrigin="anonymous"
                onLoad={(e) => {
                  const t = e.currentTarget;
                  naturalRef.current = { w: t.naturalWidth, h: t.naturalHeight };
                  // Only when there is none: a rotate clears the box first, so
                  // the rotated image gets a fresh one, while a re-render of the
                  // same image leaves the operator's box alone.
                  setRect((prev) => prev ?? initialCrop(t.width, t.height));
                  setLoadTick((k) => k + 1);
                }}
                style={{
                  maxWidth: "100%",
                  // Tall enough to work on, short enough that the Crop & Use
                  // row stays reachable without scrolling the whole form.
                  maxHeight: "70vh",
                  display: "block",
                  filter: previewFilter,
                }}
              />
              {/* The paper-removed preview, exactly over the image above. On
                  white, the way it prints; clicks fall through to the crop. */}
              {inkPreview && (
                <img
                  src={inkPreview}
                  alt=""
                  aria-hidden
                  draggable={false}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    background: "#fff",
                    pointerEvents: "none",
                  }}
                />
              )}
            </ReactCrop>
          </div>
          {/* Tone adjustment — baked into the image when it is committed. */}
          <div
            style={{
              marginTop: "8px",
              border: "1px solid #ddd",
              padding: "8px 10px",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "6px",
              }}
            >
              <span>Adjust</span>
              {!isAtStart(adj, start) && (
                <button
                  type="button"
                  onClick={() => setAdj(start)}
                  style={{ ...btn, padding: "2px 8px" }}
                >
                  Reset
                </button>
              )}
            </div>
            {(
              [
                { key: "brightness", label: "Brightness", min: 50, max: 200 },
                { key: "contrast", label: "Contrast", min: 50, max: 200 },
                { key: "saturation", label: "Saturation", min: 0, max: 200 },
                { key: "grayscale", label: "Grayscale", min: 0, max: 100 },
              ] as const
            ).map((s) => (
              <div
                key={s.key}
                style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" }}
              >
                <span style={{ width: "72px" }}>{s.label}</span>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  value={adj[s.key]}
                  onChange={(e) =>
                    setAdj((p) => ({ ...p, [s.key]: Number(e.target.value) }))
                  }
                  style={{ flex: 1 }}
                />
                <span style={{ width: "38px", textAlign: "right" }}>{adj[s.key]}%</span>
              </div>
            ))}
            <div
              style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" }}
            >
              <label
                style={{ display: "flex", alignItems: "center", gap: "6px", width: "72px" }}
                title="Print this image as pure black and white"
              >
                <input
                  type="checkbox"
                  checked={adj.threshold}
                  onChange={() => setAdj((p) => ({ ...p, threshold: !p.threshold }))}
                />
                B/W
              </label>
              {/* The cut-off: everything brighter prints white, everything
                  darker prints black. Live now, so the operator drags until the
                  text is solid and the paper is clean. 40 rather than 60 at the
                  bottom — a dark photocopy needs to go lower than that. */}
              <input
                type="range"
                min={40}
                max={240}
                value={adj.thresholdAt}
                disabled={!adj.threshold}
                title="Black / white cut-off"
                onChange={(e) =>
                  setAdj((p) => ({ ...p, thresholdAt: Number(e.target.value) }))
                }
                style={{ flex: 1 }}
              />
              <span style={{ width: "38px", textAlign: "right" }}>
                {adj.threshold ? adj.thresholdAt : "—"}
              </span>
            </div>
            {/* Signature only. Off by default, so what the operator sees in the
                form is the file the applicant actually uploaded; ticking it
                drops the paper and trims to the strokes, which is what the
                form's wide, short signature box wants when the upload is a
                photo of a signed slip. */}
            {inkOnly && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" }}
              >
                <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <input
                    type="checkbox"
                    checked={adj.ink}
                    onChange={() => setAdj((p) => ({ ...p, ink: !p.ink }))}
                  />
                  Ink only
                </label>
                <span style={{ color: "#666" }}>
                  (drop the paper behind the signature and trim to the strokes)
                </span>
              </div>
            )}
          </div>

          {/* Wraps: this workspace also runs inside the document viewer's side
              panel, which is narrower than the form, and a button pushed past
              the edge is a button the operator cannot press. */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginTop: "8px",
            }}
          >
            <button type="button" onClick={() => applyCrop(false)} style={btn}>
              Crop &amp; Use
            </button>
            <button type="button" onClick={() => applyCrop(true)} style={btn}>
              Use full image
            </button>
            {/*
              Orientation toolbar — the same four moves an image viewer offers,
              because that is what the operator already knows: turn left, turn
              right, mirror across, mirror down. One "Rotate 90°" button meant
              three clicks to straighten a document scanned the other way, and
              no way at all to fix a mirrored photo of a card.

              Icons carry a `title` each: they sit in a narrow side panel where
              four labelled buttons would not fit on one row.
            */}
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                type="button"
                onClick={() => transformImage(-1)}
                style={iconBtn}
                title="Rotate left (90° anti-clockwise)"
                aria-label="Rotate left"
              >
                ↺
              </button>
              <button
                type="button"
                onClick={() => transformImage(1)}
                style={iconBtn}
                title="Rotate right (90° clockwise)"
                aria-label="Rotate right"
              >
                ↻
              </button>
              <button
                type="button"
                onClick={() => transformImage(0, "h")}
                style={iconBtn}
                title="Flip horizontally"
                aria-label="Flip horizontally"
              >
                ⇄
              </button>
              <button
                type="button"
                onClick={() => transformImage(0, "v")}
                style={iconBtn}
                title="Flip vertically"
                aria-label="Flip vertically"
              >
                ⇅
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                setRawImage(null);
                setRect(null);
                setAdj(start);
              }}
              style={btn}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCropInput;
